// Which controls to show, from the role in the access scope. These are display hints that copy
// A2's matrix (backend `teams/service.py`: PERMISSIONS, grantable_roles, can_manage_member,
// can_edit_content). The API decides every call; a 403 or 409 shows its message and refetches the
// scope, so a stale hint corrects itself. Change this file together with that one (spec A4,
// Risks). Shared by the features, so it lives in platform/.
import type { TeamRole } from "../api/generated/model";

const RANK: Record<TeamRole, number> = { editor: 0, admin: 1, owner: 2 };
const ROLES: readonly TeamRole[] = ["owner", "admin", "editor"];

/** `manage_members`: invite, resend, revoke, change roles, remove. */
export function managesMembers(role: TeamRole): boolean {
  return role === "admin" || role === "owner";
}

/** `manage_brands`: create, replace and delete brands and products (A3). */
export function managesBrands(role: TeamRole): boolean {
  return role === "admin" || role === "owner";
}

/** `manage_billing`: the team's subscription in Stripe's Portal (spec billing/B2-4a). */
export function managesBilling(role: TeamRole): boolean {
  return role === "admin" || role === "owner";
}

/** `edit_any_content`: an admin or the owner, who may delete anyone's file, so empty the trash. */
export function editsAnyContent(role: TeamRole): boolean {
  return role === "admin" || role === "owner";
}

/** D6's content rule: an admin or the owner edits anything, anyone else what they made. */
export function canEditContent(role: TeamRole, createdBy: string, userId: string): boolean {
  return role === "admin" || role === "owner" || createdBy.toLowerCase() === userId.toLowerCase();
}

/** The roles this role may give by invitation: never above its own, and `owner` only by the
 * owner (a transfer). Highest first. */
export function invitableRoles(role: TeamRole): TeamRole[] {
  if (!managesMembers(role)) return [];
  return ROLES.filter((each) => (each === "owner" ? role === "owner" : RANK[each] <= RANK[role]));
}

/** The roles a role change may set: the owner role moves only by invitation. */
export function assignableRoles(role: TeamRole): TeamRole[] {
  return invitableRoles(role).filter((each) => each !== "owner");
}

/** Whether `actor` may change or remove a member holding `target`. Nobody manages the owner. */
export function canManageMember(actor: TeamRole, target: TeamRole): boolean {
  return target !== "owner" && managesMembers(actor) && RANK[target] <= RANK[actor];
}

export const ROLE_LABEL: Record<TeamRole, string> = {
  owner: "Owner",
  admin: "Admin",
  editor: "Editor",
};
